import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { Button, Empty, Input, List, Modal, Tag, Typography, theme } from "antd";
import type { InputRef } from "antd";
import {
  SearchOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";

import { useIsAdmin } from "@/lib/roles";
import { useNavStore, type NavItem } from "@/stores/navStore";

import { listKeypairs, listServers } from "@/features/compute/api";
import { listVolumes } from "@/features/volumes/api";
import { listNetworks, listSecurityGroups } from "@/features/networks/api";
import { listLoadBalancers } from "@/features/lbaas/api";
import { listImages } from "@/features/images/api";
import { listInstances as listDatabases } from "@/features/database/api";
import { listClusters } from "@/features/k8s/api";
import { listStacks } from "@/features/orchestration/api";
import { listZones } from "@/features/dns/api";
import { listGateways as listAgentGateways } from "@/features/agentGateway/api";
import { listShares } from "@/features/shares/api";
import { listVpnConnections } from "@/features/vpn/api";
import { listAcls } from "@/features/acls/api";
import { listFlowLogs } from "@/features/flow-logs/api";
import { listTrafficMirrors } from "@/features/trafficMirrors/api";
import { listDistributions } from "@/features/cdn/api";
import { listSecrets } from "@/features/secrets/api";
import { listBuckets } from "@/features/objects/api";
import { listQueues } from "@/features/queues/api";
import { listTopics } from "@/features/topics/api";
import { listAlarms } from "@/features/alarms/api";
import { activityPath, appViewPath, userViewPath } from "@/features/sase/analyticsQuery";
import { SASE_BASE } from "@/lib/sectionBase";

interface NamedResource {
  id: string;
  name: string;
}

interface Source {
  type: string;
  color: string;
  fetch: () => Promise<NamedResource[]>;
  route: (id: string) => string;
}

// Each source maps an existing list endpoint to {id,name} + a destination
// route. Every resource type now has a detail page, so all sources deep-link
// by id (buckets key on their name).
const SOURCES: Source[] = [
  {
    type: "Instance",
    color: "blue",
    fetch: () => listServers({ page: 1, pageSize: 50 }).then((r) => r.items),
    route: (id) => `/compute/${id}`,
  },
  {
    type: "Volume",
    color: "geekblue",
    fetch: () => listVolumes({ page: 1, pageSize: 50 }).then((r) => r.items),
    route: (id) => `/storage/${id}`,
  },
  {
    // SSH keys have no detail page and are known by name; a hit opens the list.
    type: "SSH key",
    color: "gold",
    fetch: () =>
      listKeypairs().then((rows) => rows.map((k) => ({ id: k.name, name: k.name }))),
    route: () => "/keypairs",
  },
  {
    type: "Network",
    color: "cyan",
    fetch: () => listNetworks(),
    route: (id) => `/network/${id}`,
  },
  {
    // Firewalls are a tab of the Networks page, not a page of their own.
    type: "Firewall",
    color: "red",
    fetch: () => listSecurityGroups(),
    route: () => "/network?tab=security-groups",
  },
  {
    type: "Load balancer",
    color: "purple",
    fetch: () => listLoadBalancers(),
    route: (id) => `/lbaas/${id}`,
  },
  {
    type: "Image",
    color: "green",
    fetch: () => listImages({ page: 1, pageSize: 50 }).then((r) => r.items),
    route: (id) => `/images/${id}`,
  },
  {
    type: "Database",
    color: "magenta",
    fetch: () => listDatabases(),
    route: (id) => `/database/${id}`,
  },
  {
    type: "Cluster",
    color: "volcano",
    fetch: () => listClusters(),
    route: (id) => `/k8s/${id}`,
  },
  {
    type: "Stack",
    color: "gold",
    fetch: () => listStacks(),
    route: (id) => `/orchestration/${id}`,
  },
  {
    type: "DNS zone",
    color: "lime",
    fetch: () => listZones(),
    route: (id) => `/dns/${id}`,
  },
  {
    type: "Agent gateway",
    color: "purple",
    fetch: () => listAgentGateways(),
    route: (id) => `/agent-gateways/${id}`,
  },
  {
    type: "VPN connection",
    color: "blue",
    fetch: () => listVpnConnections(),
    route: (id) => `/vpn/${id}`,
  },
  {
    type: "Network ACL",
    color: "cyan",
    fetch: () => listAcls(),
    route: (id) => `/network-acls/${id}`,
  },
  {
    // Flow logs have no detail page; the list page selects the row by id.
    type: "Flow log",
    color: "lime",
    fetch: () => listFlowLogs(),
    route: (id) => `/flow-logs?selected=${id}`,
  },
  {
    // Mirrors have no detail page; the list shows every source expanded.
    type: "Traffic mirror",
    color: "geekblue",
    fetch: () => listTrafficMirrors(),
    route: () => "/traffic-mirrors",
  },
  {
    type: "Distribution",
    color: "magenta",
    fetch: () => listDistributions(),
    route: (id) => `/content-delivery/${id}`,
  },
  {
    type: "File share",
    color: "volcano",
    fetch: () => listShares(),
    route: (id) => `/shares/${id}`,
  },
  {
    type: "Secret",
    color: "red",
    fetch: () =>
      listSecrets().then((rows) =>
        rows.map((s) => ({ id: s.id, name: s.name ?? s.id })),
      ),
    route: (id) => `/secrets/${id}`,
  },
  {
    type: "Bucket",
    color: "orange",
    fetch: () =>
      listBuckets().then((rows) =>
        rows.map((b) => ({ id: b.name, name: b.name })),
      ),
    route: (name) => `/objects/${name}`,
  },
  {
    type: "Queue",
    color: "gold",
    fetch: () => listQueues({ page: 1, pageSize: 50 }).then((r) => r.items),
    route: (id) => `/queues/${id}`,
  },
  {
    // The menu calls the page Notifications; what a customer creates is a topic.
    type: "Topic",
    color: "green",
    fetch: () => listTopics({ page: 1, pageSize: 50 }).then((r) => r.items),
    route: (id) => `/topics/${id}`,
  },
  {
    // Alarms have no detail page; a hit opens the list.
    type: "Alarm",
    color: "volcano",
    fetch: () => listAlarms({ page: 1, pageSize: 50 }).then((r) => r.items),
    route: () => "/alarms",
  },
];

/** Static console pages, so "vol" finds the Volumes page even when no
 *  resource matches. Keywords cover the words customers actually type
 *  (AWS/GCP vocabulary, abbreviations) — matched alongside the page name. */
interface PageEntry {
  name: string;
  path: string;
  keywords: readonly string[];
  /** Operator-only page: hidden from customers, who would only get the 403. */
  adminOnly?: boolean;
}

export const PAGES: readonly PageEntry[] = [
  { name: "Dashboard", path: "/", keywords: ["home", "overview"] },
  {
    name: "Instances",
    path: "/compute",
    keywords: ["vm", "server", "virtual machine", "compute"],
  },
  { name: "SSH keys", path: "/keypairs", keywords: ["ssh", "keys"] },
  {
    name: "Placement groups",
    path: "/compute/placement-groups",
    keywords: ["spread", "together", "same machine", "different machines"],
  },
  {
    name: "Launch templates",
    path: "/compute/launch-templates",
    keywords: ["template", "saved server", "launch template", "preset"],
  },
  {
    name: "Kubernetes",
    path: "/k8s",
    keywords: ["k8s", "cluster", "container"],
  },
  {
    name: "Functions",
    path: "/functions",
    keywords: ["serverless", "faas", "lambda", "function"],
  },
  {
    name: "Agent gateways",
    path: "/agent-gateways",
    keywords: ["agent", "mcp", "tool server", "model provider", "llm", "credential"],
  },
  {
    name: "Volumes",
    path: "/storage",
    keywords: ["disk", "storage", "block", "snapshot", "backup"],
  },
  { name: "Images", path: "/images", keywords: ["os", "operating system"] },
  {
    name: "Object storage",
    path: "/objects",
    keywords: ["bucket", "s3", "object", "file"],
  },
  {
    name: "Networks",
    path: "/network",
    keywords: [
      "networking",
      "subnet",
      "router",
      "floating ip",
      "security group",
      "firewall",
      "port",
      "vlan",
    ],
  },
  {
    name: "VPN",
    path: "/vpn",
    keywords: ["vpn", "vpn connections", "tunnel", "site-to-site", "ipsec", "access"],
  },
  {
    name: "Network ACLs",
    path: "/network-acls",
    keywords: ["acl", "access control", "router rules", "allow", "deny", "firewall"],
  },
  {
    name: "Flow logs",
    path: "/flow-logs",
    keywords: ["flow", "traffic log", "accepted", "dropped", "connections"],
  },
  {
    name: "Traffic mirroring",
    path: "/traffic-mirrors",
    keywords: ["mirror", "packet capture", "span", "inspect traffic", "monitor server"],
  },
  {
    name: "Content delivery",
    path: "/content-delivery",
    keywords: ["distribution", "cache", "origin", "invalidation", "static site", "cdn", "cloudfront"],
  },
  {
    name: "Video streaming",
    path: "/video-streaming",
    keywords: ["video", "streaming", "upload video", "player", "embed", "playback", "signing key", "media"],
  },
  {
    name: "File shares",
    path: "/shares",
    keywords: ["nfs", "file share", "storage", "mount"],
  },
  {
    name: "Load balancers",
    path: "/lbaas",
    keywords: ["lb", "listener", "pool", "balancer"],
  },
  { name: "DNS", path: "/dns", keywords: ["zone", "domain", "record"] },
  {
    name: "Databases",
    path: "/database",
    keywords: ["db", "mysql", "sql", "rds"],
  },
  {
    name: "Queues",
    path: "/queues",
    keywords: ["queue", "message", "messaging", "dead-letter", "sqs"],
  },
  {
    name: "Notifications",
    path: "/topics",
    keywords: ["topic", "subscription", "publish", "pub/sub", "fan-out", "sns"],
  },
  {
    name: "Alarms",
    path: "/alarms",
    keywords: ["alert", "monitoring", "metric", "cloudwatch"],
  },
  {
    name: "Secrets",
    path: "/secrets",
    keywords: ["certificate", "vault", "key management"],
  },
  {
    name: "Credentials",
    path: "/credentials",
    keywords: ["api key", "access key", "application credential", "ec2"],
  },
  {
    name: "Orchestration",
    path: "/orchestration",
    keywords: ["stack", "template", "cloudformation"],
  },
  {
    name: "Billing",
    path: "/billing",
    keywords: ["cost", "invoice", "spend", "usage", "price"],
  },
  {
    name: "Audit log",
    path: "/audit",
    keywords: ["activity", "history", "logs", "events"],
  },
  {
    name: "Account",
    path: "/account",
    keywords: ["settings", "profile", "password", "mfa", "two-factor", "tour"],
  },
  {
    name: "Logs",
    path: "/logs",
    keywords: ["logs", "log search", "errors", "troubleshoot"],
    adminOnly: true,
  },
] as const;

/** Words customers type for the SASE pages (plan §7.1 rule 6: Cmd+K over
 *  SASE objects). The pages themselves come from the account's menu, so a
 *  page appears only when its gate is open. */
const SASE_KEYWORDS: Record<string, readonly string[]> = {
  "/sase": ["sase", "overview", "home", "zero trust", "sse"],
  "/sase/get-started": ["onboarding", "setup", "checklist", "start"],
  "/sase/private-access": ["ztna", "private apps", "connector", "access policy", "vpn"],
  "/sase/ai/agent-gateways": ["agent", "mcp", "llm", "ai security", "tool server", "model"],
  "/sase/users": ["users", "groups", "devices", "sign-in", "identity", "sso", "scim"],
  "/sase/incidents": ["threat", "alert", "malware", "attack", "ips", "security events"],
  "/sase/analytics": [
    "logs",
    "activity",
    "search",
    "traffic",
    "top users",
    "top sites",
    "report",
    "export",
  ],
};

/** Where Cmd+K may send this account (the account's product view, SD-01).
 *  In the *SASE only* view it offers SASE destinations alone: no cloud page,
 *  no cloud resource, no cloud recents (plan §7.1 rules 1 and 3). */
export interface SearchScope {
  saseOnly: boolean;
  /** The SASE pages the account's menu shows (its gates already applied). */
  sasePages: readonly { name: string; path: string }[];
  /** SASE → AI security → Agent gateways is open. */
  saseAgentGateways: boolean;
  /** SASE → Analytics is open (search activity from Cmd+K). */
  saseAnalytics: boolean;
}

const CLOUD_SCOPE: SearchScope = {
  saseOnly: false,
  sasePages: [],
  saseAgentGateways: false,
  saseAnalytics: false,
};

function isSasePath(path: string): boolean {
  return /^\/sase(\/|\?|$)/.test(path);
}

/** The agent gateways through their SASE door. */
const SASE_GATEWAY_SOURCE: Source = {
  type: "Agent gateway",
  color: "purple",
  fetch: () => listAgentGateways(),
  route: (id) => `${SASE_BASE.agentGateways}/${id}`,
};

/** "Search activity for …" and, when the words look like one, the user or
 *  app view (SASE-14). */
function activityHits(q: string): Hit[] {
  const hits: Hit[] = [
    {
      type: "Activity",
      color: "cyan",
      id: `search:${q}`,
      name: `Search activity for "${q}"`,
      route: activityPath({ q }),
    },
  ];
  if (/^\S+@\S+$/.test(q))
    hits.push({
      type: "Activity",
      color: "cyan",
      id: `user:${q}`,
      name: `Everything ${q} did`,
      route: userViewPath(q),
    });
  else if (/^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(q))
    hits.push({
      type: "Activity",
      color: "cyan",
      id: `app:${q}`,
      name: `Everything done with ${q}`,
      route: appViewPath(q),
    });
  return hits;
}

/**
 * Page match quality: name exact/prefix/substring beat keyword matches, so a
 * page group holding an exact name hit can outrank resource groups.
 */
function rankPage(entry: PageEntry, q: string): number | null {
  const name = entry.name.toLowerCase();
  if (name === q) return 0;
  if (name.startsWith(q)) return 1;
  if (name.includes(q)) return 2;
  if (entry.keywords.some((k) => k.startsWith(q))) return 3;
  if (entry.keywords.some((k) => k.includes(q))) return 4;
  return null;
}

/** Cap per type — both what we display and what we tell the user about. */
const MAX_PER_TYPE = 50;

interface Hit {
  type: string;
  color: string;
  id: string;
  name: string;
  route: string;
}

interface HitGroup {
  type: string;
  color: string;
  hits: Hit[];
  /** Flat index (across all groups) of this group's first hit. */
  startIndex: number;
  /** True when matches beyond MAX_PER_TYPE were cut off. */
  truncated: boolean;
  /** Best (lowest) match rank in the group — used to order groups. */
  bestRank: number;
}

/**
 * Match quality: exact name < name prefix < name substring < UUID/id match.
 * Returns null for a non-match.
 */
function rankMatch(item: NamedResource, q: string): number | null {
  const name = (item.name ?? "").toLowerCase();
  const id = (item.id ?? "").toLowerCase();
  if (name === q) return 0;
  if (name.startsWith(q)) return 1;
  if (name.includes(q)) return 2;
  if (id.includes(q)) return 3;
  return null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Where the account's product view lets search go (default: the cloud). */
  scope?: SearchScope;
}

/** A pinnable quick-jump row used in the empty-state launcher. */
function QuickList({
  title,
  items,
  favorites,
  onGo,
  onToggleFav,
}: {
  title: string;
  items: NavItem[];
  favorites: NavItem[];
  onGo: (path: string) => void;
  onToggleFav: (item: NavItem) => void;
}) {
  const favSet = new Set(favorites.map((f) => f.path));
  return (
    <>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {title}
      </Typography.Text>
      <List<NavItem>
        size="small"
        dataSource={items}
        rowKey={(i) => i.path}
        renderItem={(item) => {
          const fav = favSet.has(item.path);
          return (
            <List.Item
              style={{ cursor: "pointer" }}
              onClick={() => onGo(item.path)}
              actions={[
                <Button
                  key="fav"
                  type="text"
                  size="small"
                  aria-label={fav ? "Unfavorite" : "Favorite"}
                  icon={
                    fav ? (
                      <StarFilled style={{ color: "#faad14" }} />
                    ) : (
                      <StarOutlined />
                    )
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFav(item);
                  }}
                />,
              ]}
            >
              <List.Item.Meta title={item.label} />
            </List.Item>
          );
        }}
      />
    </>
  );
}

function GlobalSearch({ open, onClose, scope = CLOUD_SCOPE }: Props) {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const admin = useIsAdmin();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const activeRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<InputRef>(null);
  const allFavorites = useNavStore((s) => s.favorites);
  const allRecents = useNavStore((s) => s.recents);
  const toggleFavorite = useNavStore((s) => s.toggleFavorite);
  const { saseOnly } = scope;
  const { pathname } = useLocation();
  // Activity search is offered where SASE is what the account is looking at.
  const offerActivity = scope.saseAnalytics && (saseOnly || isSasePath(pathname));
  // In the SASE only view a pin or a visit to a cloud page is not offered.
  const favorites = useMemo(
    () => (saseOnly ? allFavorites.filter((f) => isSasePath(f.path)) : allFavorites),
    [saseOnly, allFavorites],
  );
  const recents = useMemo(
    () => (saseOnly ? allRecents.filter((r) => isSasePath(r.path)) : allRecents),
    [saseOnly, allRecents],
  );
  const sources = useMemo<Source[]>(
    () => (saseOnly ? (scope.saseAgentGateways ? [SASE_GATEWAY_SOURCE] : []) : SOURCES),
    [saseOnly, scope.saseAgentGateways],
  );
  const pages = useMemo<PageEntry[]>(() => {
    const sase = scope.sasePages.map((p) => ({
      name: p.name,
      path: p.path,
      keywords: SASE_KEYWORDS[p.path] ?? [],
    }));
    return saseOnly ? sase : [...PAGES, ...sase];
  }, [saseOnly, scope.sasePages]);

  // Fetch only once the user actually types — the empty state renders
  // favorites/recents and needs no data, so opening the palette must not
  // fan out 11 list requests. Results are cached 30s across keystrokes.
  const searching = open && query.trim().length >= 2;
  const results = useQueries({
    queries: sources.map((source) => ({
      queryKey: ["global-search", source.type, saseOnly ? "sase" : "cloud"],
      queryFn: source.fetch,
      enabled: searching,
      staleTime: 30_000,
    })),
  });

  // Group hits by resource type, ranked exact > prefix > substring > id match
  // within each group; groups themselves order by their best match, so the
  // type holding the exact hit surfaces first.
  const groups = useMemo<HitGroup[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const out: HitGroup[] = [];
    // Console pages come from a static table — no fetch, but same 2-char
    // gate as the resource sources so a single keystroke stays silent.
    if (q.length >= 2) {
      const pageMatches = pages.filter((entry) => admin || !entry.adminOnly)
        .map((entry) => ({ rank: rankPage(entry, q), entry }))
        .filter((m): m is { rank: number; entry: PageEntry } => m.rank !== null);
      if (pageMatches.length > 0) {
        pageMatches.sort(
          (a, b) => a.rank - b.rank || a.entry.name.localeCompare(b.entry.name),
        );
        out.push({
          type: "Page",
          color: "default",
          hits: pageMatches.map(({ entry }) => ({
            type: "Page",
            color: "default",
            id: entry.path,
            name: entry.name,
            route: entry.path,
          })),
          startIndex: 0,
          truncated: false,
          bestRank: pageMatches[0]?.rank ?? 4,
        });
      }
    }
    sources.forEach((source, i) => {
      const data = results[i]?.data ?? [];
      const matched: Array<{ rank: number; hit: Hit }> = [];
      for (const item of data) {
        const rank = rankMatch(item, q);
        if (rank === null) continue;
        matched.push({
          rank,
          hit: {
            type: source.type,
            color: source.color,
            id: item.id,
            name: item.name,
            route: source.route(item.id),
          },
        });
      }
      if (matched.length === 0) return;
      matched.sort(
        (a, b) => a.rank - b.rank || a.hit.name.localeCompare(b.hit.name),
      );
      out.push({
        type: source.type,
        color: source.color,
        hits: matched.slice(0, MAX_PER_TYPE).map((m) => m.hit),
        startIndex: 0, // filled in below, once groups are ordered
        truncated: matched.length > MAX_PER_TYPE,
        bestRank: matched[0]?.rank ?? 3,
      });
    });
    out.sort((a, b) => a.bestRank - b.bestRank);
    // Activity search is a destination, not a match: it always comes last.
    if (offerActivity && q.length >= 2) {
      out.push({
        type: "Activity",
        color: "cyan",
        hits: activityHits(query.trim()),
        startIndex: 0,
        truncated: false,
        bestRank: 9,
      });
    }
    let acc = 0;
    for (const group of out) {
      group.startIndex = acc;
      acc += group.hits.length;
    }
    return out;
  }, [query, results, admin, pages, sources, offerActivity]);

  const flatHits = useMemo(() => groups.flatMap((g) => g.hits), [groups]);

  // New query → highlight the top-ranked hit again.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Keep the keyboard-highlighted option in view. (scrollIntoView is missing
  // in jsdom — guard rather than crash the suite.)
  useEffect(() => {
    if (typeof activeRef.current?.scrollIntoView === "function") {
      activeRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, groups]);

  const go = (route: string) => {
    navigate(route);
    setQuery("");
    onClose();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (flatHits.length) setActiveIndex((i) => (i + 1) % flatHits.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (flatHits.length) {
        setActiveIndex((i) => (i - 1 + flatHits.length) % flatHits.length);
      }
    } else if (e.key === "Enter") {
      const hit = flatHits[activeIndex];
      if (hit) go(hit.route);
    } else if (e.key === "Escape") {
      setQuery("");
      onClose();
    }
  };

  const clampedActive = Math.min(activeIndex, Math.max(flatHits.length - 1, 0));

  return (
    <Modal
      open={open}
      onCancel={() => {
        setQuery("");
        onClose();
      }}
      footer={null}
      destroyOnHidden
      // UI-08: the dialog takes focus for itself once it has opened, after
      // the field's own `autoFocus` has run — so put the cursor in the field
      // then, or the first keys typed go nowhere.
      afterOpenChange={(opened) => {
        if (opened) inputRef.current?.focus();
      }}
      styles={{ body: { paddingTop: 8 } }}
    >
      <Input
        ref={inputRef}
        autoFocus
        size="large"
        prefix={<SearchOutlined />}
        placeholder={
          saseOnly
            ? "Search SASE pages, gateways and activity…"
            : "Search instances, volumes, networks, load balancers…"
        }
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        allowClear
        role="combobox"
        aria-expanded={flatHits.length > 0}
        aria-controls="global-search-results"
        aria-activedescendant={
          flatHits.length > 0
            ? `global-search-option-${clampedActive}`
            : undefined
        }
      />
      <div style={{ marginTop: 12, maxHeight: 420, overflow: "auto" }}>
        {query.trim() === "" ? (
          favorites.length === 0 && recents.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Type to search — visited pages and pins appear here"
            />
          ) : (
            <>
              {favorites.length > 0 && (
                <QuickList
                  title="Favorites"
                  items={favorites}
                  favorites={favorites}
                  onGo={go}
                  onToggleFav={toggleFavorite}
                />
              )}
              {recents.filter((r) => !favorites.some((f) => f.path === r.path))
                .length > 0 && (
                <QuickList
                  title="Recent"
                  items={recents.filter(
                    (r) => !favorites.some((f) => f.path === r.path),
                  )}
                  favorites={favorites}
                  onGo={go}
                  onToggleFav={toggleFavorite}
                />
              )}
            </>
          )
        ) : flatHits.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No matching resources"
          />
        ) : (
          <div id="global-search-results" role="listbox" aria-label="Search results">
            {groups.map((group) => (
              <div key={group.type}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 4px 4px",
                  }}
                >
                  <Tag color={group.color} style={{ marginInlineEnd: 0 }}>
                    {group.type}
                  </Tag>
                  {group.truncated && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      showing first {MAX_PER_TYPE}
                    </Typography.Text>
                  )}
                </div>
                {group.hits.map((hit, i) => {
                  const index = group.startIndex + i;
                  const isActive = index === clampedActive;
                  return (
                    <div
                      key={`${hit.type}-${hit.id}`}
                      {...(isActive ? { ref: activeRef } : {})}
                      id={`global-search-option-${index}`}
                      role="option"
                      aria-selected={isActive}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => go(hit.route)}
                      style={{
                        padding: "6px 8px",
                        borderRadius: token.borderRadiusSM,
                        cursor: "pointer",
                        background: isActive
                          ? token.controlItemBgActive
                          : undefined,
                        // Visible focus ring for the keyboard highlight, on
                        // top of the background tint.
                        outline: isActive
                          ? `1px solid ${token.colorPrimaryBorder}`
                          : undefined,
                      }}
                    >
                      <div>{hit.name}</div>
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        {hit.id}
                      </Typography.Text>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default GlobalSearch;
